import { useMemo } from 'react';

import { DropdownOption } from '@aslanpay/portal-common-lib';

import usePots, { companyPotQueryParams } from 'app/hooks/usePots';
import useGroupsWithPot from 'app/hooks/useGroupsWithPot/useGroupsWithPot';
import { getGroupDisplayNameWithParents } from 'app/utils/groupsWithPot/groupsWithPot';

/**
 * Every pot a company's points can be moved to or from, as dropdown options.
 * <p>
 * Three kinds: the company wallet, a pride's pot and a campaign's funding pot. An employee's pot is not among
 * them and is never asked for — points reach a person by being awarded, not by being moved into their wallet.
 * Both ends of a transfer read the same list, so what can be a source can be a destination and neither end
 * needs its own rule about what exists.
 *
 * @param excludePotId the pot at the other end, which cannot also be this one
 */
function usePotOptions(companyId: string, excludePotId?: string) {
  const { data: companyPots, isLoading: isLoadingCompanyPot } = usePots.useFetchPots(
    companyPotQueryParams(companyId),
    !!companyId
  );

  const { data: campaignPots, isLoading: isLoadingCampaignPots } = usePots.useFetchPots(
    {
      companyIds: [companyId],
      relatedType: ['CAMPAIGN'],
      status: ['ACTIVE'],
      page: 0,
      size: 200,
    },
    !!companyId
  );

  const { flatGroupsWithPots, isLoading: isLoadingPrides } = useGroupsWithPot(
    companyId,
    !!companyId
  );

  const companyPot = companyPots?.list?.[0];

  const options: DropdownOption[] = useMemo(
    () =>
      [
        {
          text: 'Company Wallet',
          name: 'Company Wallet',
          value: companyPot?.id || '',
          points: companyPot?.balance || 0,
        },
        ...flatGroupsWithPots
          .filter((group) => group.pot)
          .map((group) => ({
            text: getGroupDisplayNameWithParents(group),
            name: group.name,
            value: group.pot?.id || '',
            points: group.pot?.balance || 0,
            groupName: 'Pots',
          })),
        ...(campaignPots?.list ?? []).map((pot) => ({
          text: pot.name,
          name: pot.name,
          value: pot.id,
          points: pot.balance,
          groupName: 'Campaigns',
        })),
      ].filter((option) => option.value && option.value !== excludePotId),
    [companyPot, flatGroupsWithPots, campaignPots, excludePotId]
  );

  return {
    options,
    companyPot,
    isLoading: isLoadingCompanyPot || isLoadingCampaignPots || isLoadingPrides,
  };
}

export default usePotOptions;
